import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { colors } from '@/constants/colors';

/**
 * "You need something else first" — said once, in a modal, with the way there.
 *
 * ─── WHY THIS SHAPE, AFTER TWO OTHERS ───
 * Ray tried greying the sidebar out (*"it looks weird"*) and a full-screen setup
 * guide (*"lets keep the UI no steps guide"*) before landing here, 2026-08-31:
 * *"we just gonna gate the button… it should show a modal message, go to create
 * server."*
 *
 * It is the better answer for a reason worth writing down. The other two paid
 * the cost of the dependency ALL THE TIME — a greyed menu nags on every screen,
 * a setup wizard blocks the whole product — whereas this one costs nothing until
 * somebody actually reaches for the thing they cannot do yet. And at that exact
 * moment they have shown you what they were trying to achieve, so the answer can
 * be a door rather than a lecture.
 *
 * The CTA NAVIGATES. A message that says "set up your business first" and leaves
 * them to find Settings is half an answer.
 */
interface Props {
   title: string;
   /** What is missing, and why it is needed — in the operator's terms. */
   message: string;
   ctaLabel: string;
   ctaHref: string;
   onCancel: () => void;
}

const IspNeedsSetup: React.FC<Props> = ({
   title,
   message,
   ctaLabel,
   ctaHref,
   onCancel,
}) => {
   const navigate = useNavigate();
   return (
      <Wrapper>
         <Title>{title}</Title>
         <Message>{message}</Message>
         <Actions>
            <Ghost type="button" onClick={onCancel}>
               Not now
            </Ghost>
            <Primary
               type="button"
               onClick={() => {
                  onCancel();
                  navigate(ctaHref);
               }}
            >
               {ctaLabel}
            </Primary>
         </Actions>
      </Wrapper>
   );
};

export default IspNeedsSetup;

const Wrapper = styled.div`
   width: 460px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const Title = styled.h2`
   margin: 0;
   font-size: 16px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const Message = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.65;
   color: ${colors.GRAY5};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 6px;
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;

const Primary = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
`;
